import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { qk } from '@/lib/queryKeys';
import { useTenantId } from './useTenant';

/**
 * Write side of the reference data — quarries (and their price lists),
 * client sites, materials, and the tenant settings that pricing depends on
 * (VAT rate, dwell policy).
 *
 * Reads live in useDirectory.js and are shared with the dispatcher views, so
 * adding a quarry here immediately updates the map and the order form
 * without either of them knowing this screen exists.
 */

function useDirectoryInvalidation() {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return (...prefixes) => {
    prefixes.forEach((prefix) => queryClient.invalidateQueries({ queryKey: [prefix, tenantId] }));
  };
}

/* ---------------------------------- quarries --------------------------- */

export const QUARRY_CATEGORIES = ['own', 'competitor'];

export function useSaveQuarry() {
  const tenantId = useTenantId();
  const invalidate = useDirectoryInvalidation();
  return useMutation({
    mutationFn: ({ quarryId, ...payload }) =>
      quarryId
        ? api(`/api/quarries/${quarryId}`, { method: 'PUT', body: payload })
        : api(`/api/tenants/${tenantId}/quarries`, { method: 'POST', body: payload }),
    onSuccess: () => invalidate('quarries'),
  });
}

export function useDeleteQuarry() {
  const invalidate = useDirectoryInvalidation();
  return useMutation({
    mutationFn: (quarryId) => api(`/api/quarries/${quarryId}`, { method: 'DELETE' }),
    onSuccess: () => invalidate('quarries'),
  });
}

/** The way back out of the archive. `active: true` also clears the backend's
 *  deleted_at stamp (core/archive.py), so a quarry archived again later reads
 *  as archived on the second date rather than the first. */
export function useRestoreQuarry() {
  const invalidate = useDirectoryInvalidation();
  return useMutation({
    mutationFn: (quarryId) => api(`/api/quarries/${quarryId}`, { method: 'PUT', body: { active: true } }),
    onSuccess: () => invalidate('quarries'),
  });
}

/** Kainodara: one price per material per quarry, which is what the order
 *  price estimate reads. Fetched per quarry, on expand — a fleet with forty
 *  quarries should not load forty price lists to show a table of names. */
export function useQuarryPrices(quarryId) {
  return useQuery({
    queryKey: ['quarry-prices', quarryId],
    queryFn: () => api(`/api/quarries/${quarryId}/prices`),
    enabled: Boolean(quarryId),
    placeholderData: [],
  });
}

export function useSaveQuarryPrice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ quarryId, priceId, ...payload }) =>
      priceId
        ? api(`/api/quarry-prices/${priceId}`, { method: 'PUT', body: payload })
        : api(`/api/quarries/${quarryId}/prices`, { method: 'POST', body: payload }),
    onSuccess: (_data, { quarryId }) => {
      queryClient.invalidateQueries({ queryKey: ['quarry-prices', quarryId] });
      // V-37: a price row now carries the purchase cost too, and both sides
      // of it feed every margin on the Analytics tab. Without this a
      // dispatcher who fills in what the quarry charges goes back to a tab
      // still showing the margin that ignored it, and concludes the field
      // does nothing.
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      // V-91, and the comment above says why it matters in the same words:
      // a dispatcher who fills in the rate the order form just told him was
      // missing goes back to a form still showing "Kainoraštyje nėra
      // kainos", and concludes the warning is broken. Cleared by quarry
      // prefix, because the check's answer depends on the whole of that
      // quarry's list — including which materials it names.
      queryClient.invalidateQueries({ queryKey: qk.quarryPriceChecks(quarryId) });
    },
  });
}

export function useDeleteQuarryPrice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ priceId }) => api(`/api/quarry-prices/${priceId}`, { method: 'DELETE' }),
    onSuccess: (_data, { quarryId }) => {
      queryClient.invalidateQueries({ queryKey: ['quarry-prices', quarryId] });
      // V-277. useSaveQuarryPrice above has carried this since V-37 — a
      // price row's cost/rate feed every margin on the Analytics tab,
      // deleting one changes reporting.py's live PRICE_SUBSELECT join just
      // as much as editing one does. Without this a dispatcher who removes
      // a duplicate or corrected price goes back to Analytics still showing
      // the margin computed with the row that no longer exists.
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      // Deleting the last rate for a material has to bring the warning
      // BACK, for the same reason adding one has to take it away.
      queryClient.invalidateQueries({ queryKey: qk.quarryPriceChecks(quarryId) });
    },
  });
}

/* ---------------------- transporto kainodaros modeliai ------------------ */
/* Tenant-scoped (not quarry-scoped like quarry_prices above), so a plain
   flat query key — the admin console only ever holds one tenant's session
   at a time, same reasoning as useMaterials()'s own key shape. */

export function useTonKmRateTiers() {
  return useQuery({
    queryKey: ['tonkm-rate-tiers'],
    queryFn: () => api('/api/tonkm-rate-tiers'),
    placeholderData: [],
  });
}

export function useSaveTonKmRateTier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tierId, ...payload }) =>
      tierId
        ? api(`/api/tonkm-rate-tiers/${tierId}`, { method: 'PUT', body: payload })
        : api('/api/tonkm-rate-tiers', { method: 'POST', body: payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tonkm-rate-tiers'] }),
  });
}

export function useDeleteTonKmRateTier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tierId) => api(`/api/tonkm-rate-tiers/${tierId}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tonkm-rate-tiers'] }),
  });
}

export function useDwellRateTiers() {
  return useQuery({
    queryKey: ['dwell-rate-tiers'],
    queryFn: () => api('/api/dwell-rate-tiers'),
    placeholderData: [],
  });
}

export function useSaveDwellRateTier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tierId, ...payload }) =>
      tierId
        ? api(`/api/dwell-rate-tiers/${tierId}`, { method: 'PUT', body: payload })
        : api('/api/dwell-rate-tiers', { method: 'POST', body: payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dwell-rate-tiers'] }),
  });
}

export function useDeleteDwellRateTier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tierId) => api(`/api/dwell-rate-tiers/${tierId}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dwell-rate-tiers'] }),
  });
}

/* ----------------------------------- sites ----------------------------- */

export function useSaveSite() {
  const tenantId = useTenantId();
  const invalidate = useDirectoryInvalidation();
  return useMutation({
    mutationFn: ({ siteId, ...payload }) =>
      siteId
        ? api(`/api/sites/${siteId}`, { method: 'PUT', body: payload })
        : api(`/api/tenants/${tenantId}/sites`, { method: 'POST', body: payload }),
    onSuccess: () => invalidate('sites'),
  });
}

export function useDeleteSite() {
  const invalidate = useDirectoryInvalidation();
  return useMutation({
    mutationFn: (siteId) => api(`/api/sites/${siteId}`, { method: 'DELETE' }),
    onSuccess: () => invalidate('sites'),
  });
}

/** Mirror of useRestoreQuarry — see its comment. */
export function useRestoreSite() {
  const invalidate = useDirectoryInvalidation();
  return useMutation({
    mutationFn: (siteId) => api(`/api/sites/${siteId}`, { method: 'PUT', body: { active: true } }),
    onSuccess: () => invalidate('sites'),
  });
}

/* --------------------------------- materials --------------------------- */

export function useSaveMaterial() {
  const tenantId = useTenantId();
  const invalidate = useDirectoryInvalidation();
  return useMutation({
    mutationFn: ({ materialId, ...payload }) =>
      materialId
        ? api(`/api/materials/${materialId}`, { method: 'PUT', body: payload })
        : api(`/api/tenants/${tenantId}/materials`, { method: 'POST', body: payload }),
    onSuccess: () => invalidate('materials'),
  });
}

export function useDeleteMaterial() {
  const invalidate = useDirectoryInvalidation();
  return useMutation({
    mutationFn: (materialId) => api(`/api/materials/${materialId}`, { method: 'DELETE' }),
    onSuccess: () => invalidate('materials'),
  });
}

/* ----------------------------- tenant settings ------------------------- */

/**
 * VAT rate and the dwell policy (free minutes at quarry/site, hourly rate).
 * PUT accepts each field independently, so the two settings forms can save
 * their own field without clobbering the other's — which is why this takes a
 * partial patch rather than the whole tenant.
 */
export function useUpdateTenantSettings() {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch) => api(`/api/tenants/${tenantId}`, { method: 'PUT', body: patch }),
    onSuccess: () => {
      // The tenant row is served by the list endpoint (see useTenant), so
      // that is the one cache entry every field this form saves needs.
      queryClient.invalidateQueries({ queryKey: qk.tenants() });
      // V-279. fuel_cost_per_km/toll_cost_per_km/driver_cost_per_hour_eur
      // are read straight off this same row by reporting.py's _analytics()
      // and multiplied into every margin on that tab — the same reasoning
      // useSaveQuarryPrice/useDeleteQuarryPrice above already carry for a
      // quarry price row (V-37/V-277). Without this, correcting the fuel
      // assumption here and switching to Analytics (a same-tab component
      // swap, not a reload) can show up to 60s of margins computed with the
      // rate just replaced.
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
    },
  });
}
